import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { AdminTaskNotificationDto } from './admin-notifications-api.service';

const HUB_PATHS = [
  '/api/movies/hubs/catalog',
  '/api/masterdata/hubs/catalog',
  '/api/series/hubs/catalog',
  '/api/jobs/hubs/catalog'
];

const NOTIFICATIONS_HUB = '/api/users/hubs/notifications';

@Injectable({
  providedIn: 'root'
})
export class CatalogRealtimeService implements OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly changedSubject = new Subject<string>();
  private readonly notificationSubject = new Subject<AdminTaskNotificationDto>();
  private readonly connections: HubConnection[] = [];
  private started = false;

  readonly catalogChanged$ = this.changedSubject.asObservable();
  readonly notificationCreated$ = this.notificationSubject.asObservable();

  async start(): Promise<void> {
    if (this.started || typeof window === 'undefined') {
      return;
    }

    this.started = true;
    const base = typeof window !== 'undefined' ? window.location.origin : '';

    for (const path of HUB_PATHS) {
      const connection = new HubConnectionBuilder()
        .withUrl(`${base}${path}`)
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Error)
        .build();

      connection.on('catalogChanged', (payload: { scope?: string } | undefined) => {
        this.zone.run(() => {
          this.changedSubject.next(payload?.scope ?? 'catalog');
        });
      });

      this.connections.push(connection);
    }

    const notifications = new HubConnectionBuilder()
      .withUrl(`${base}${NOTIFICATIONS_HUB}`)
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Error)
      .build();
    notifications.on('notificationCreated', (payload: AdminTaskNotificationDto) => {
      this.zone.run(() => this.notificationSubject.next(payload));
    });
    this.connections.push(notifications);

    await Promise.all(
      this.connections.map(async (connection) => {
        try {
          await connection.start();
        } catch {
          // Hub may not be deployed yet; home still loads via HTTP.
        }
      })
    );
  }

  ngOnDestroy(): void {
    void Promise.all(this.connections.map((connection) => connection.stop()));
    this.changedSubject.complete();
    this.notificationSubject.complete();
  }
}
